<template>
  <div :id="`${source}_transfer_call`">
    <SkeletonLoader
      v-if="isLoading"
      :rsk-size="36"
      :page="loaderPages.TRANSFER_CALL"
    />

    <div v-else class="flex flex-col">
      <div class="flex flex-col">
        <div class="p-2">
          <CustomInput
            format="text"
            v-model="searchQuery"
            placeholder="Search user"
            type="primary"
            :auto-focus="true"
            :icon="imageNames.SEARCH_VARIANT1"
            size="size-4"
            alt="search_icon"
            @input="handleInputSearch"
          />
        </div>

        <div class="flex flex-col px-2 pb-2 h-[210px] overflow-y-scroll">
          <div
            v-if="displayItems?.length > 0"
            v-for="item in displayItems"
            :key="item.id"
            class="flex flex-row items-center p-2 gap-3 cursor-pointer hover_effect"
            :class="{
              selected: selectedItem?.id === item.id,
              'opacity-60 cursor-not-allowed':
                hasInboundAvailabilityAndFalse(item),
            }"
            @click="handleItemSelect(item)"
          >
            <div class="relative">
              <ProfileIcon :size="32" :font="14" :data="item" />
              <ThemeImage
                v-if="item?.type === numberOwnerTypes.USER"
                class="absolute -bottom-[2px] -right-[2px] size-3"
                :icon="
                  shouldShowActiveDot(item)
                    ? imageNames.ACTIVE_DOT
                    : imageNames.INACTIVE_DOT
                "
                alt="availability_dot"
              />
            </div>

            <div class="flex flex-col gap-1">
              <span class="text-sm">{{ item?.name }}</span>
              <span class="text-[11px] opacity-60">{{ item.type }}</span>
            </div>
          </div>

          <EmptyScreen
            v-else
            source="transfer_call"
            text="No users/teams found"
          />
        </div>
      </div>

      <div
        v-if="showAskFirst"
        class="flex flex-row gap-2 p-4 border_top items-center justify-center"
      >
        <a-button
          class="secondary_medium w-full"
          @click="handleWarmTransfer"
          :loading="isWarmTransferring"
          :disabled="!isFormValid"
          >{{ isWarmTransferring ? "Asking.." : "Ask First" }}</a-button
        >

        <a-button
          class="primary_medium w-full"
          @click="handleToTransferCall"
          :loading="isTransferringCall"
          :disabled="!isFormValid"
        >
          {{ isTransferringCall ? "Transferring.." : "Transfer Now" }}
        </a-button>
      </div>

      <div v-else class="flex flex-row justify-end p-4 border_top">
        <a-button
          class="primary_medium w-fit"
          @click="handleToTransferCall"
          :loading="isTransferringCall"
          :disabled="!isFormValid"
        >
          {{ isTransferringCall ? "Transferring.." : "Transfer Now" }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { validationEntities } from "@/src/constants/callConstants.js";
import { toFetchTeams, toFetchUsers } from "@/src/utils/userUtils.js";
import {
  assignCallTypes,
  numberOwnerTypes,
} from "@/src/constants/callConstants.js";
import {
  toGetPhoneNumbers,
  toExecuteWarmTransfer,
  toTransferCall,
  validatePhoneNumber,
} from "@/src/utils/callsUtils.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const props = defineProps({
  source: { type: String, required: true },
});

const searchQuery = ref(null);
const isLoading = ref(false);
const isTransferringCall = ref(false);
const isWarmTransferring = ref(false);
const usersList = ref([]);
const teamsList = ref([]);
const selectedItem = ref(null);
const phoneNumberItem = ref(null);
const showAskFirst = ref(localStorage.getItem("calls_warm_transfer"));

const formState = reactive({
  type: assignCallTypes.USER,
  typeId: null,
  transferNumber: "",
});

const hasInboundAvailabilityAndFalse = (item) => {
  return "inbound_availability" in item && !item.inbound_availability;
};

const shouldShowActiveDot = (item) => {
  return !("inbound_availability" in item) || item.inbound_availability;
};

const allItems = computed(() => {
  const users = usersList.value.map((user) => ({
    ...user,
    type: "User",
  }));
  const teams = teamsList.value.map((team) => ({
    ...team,
    type: "Team",
  }));
  return [...users, ...teams];
});

const filteredItems = computed(() => {
  if (!searchQuery.value) return allItems.value;

  const query = String(searchQuery.value).toLowerCase();
  return allItems.value.filter((item) =>
    String(item.name).toLowerCase().includes(query),
  );
});

const displayItems = computed(() => {
  return phoneNumberItem.value
    ? [...filteredItems.value, phoneNumberItem.value]
    : filteredItems.value;
});

const isFormValid = computed(() => {
  return Boolean(selectedItem.value);
});

const handleItemSelect = (item) => {
  try {
    if (hasInboundAvailabilityAndFalse(item)) return;

    selectedItem.value = item;
    formState.type = item.isPhoneNumber
      ? assignCallTypes.PHONE_NUMBER
      : item.type === "User"
        ? assignCallTypes.USER
        : assignCallTypes.TEAM;
    formState.typeId = item.isPhoneNumber ? null : item.id;
    formState.transferNumber = item.isPhoneNumber ? item.name : "";
  } catch (error) {
    console.error("An error occurred while handling item select: ", error);
  }
};

const handleInputSearch = (value) => {
  try {
    if (typeof value === "object" && value.target) {
      searchQuery.value = value.target.value?.trim();
    } else {
      searchQuery.value = value?.trim();
    }

    if (
      validatePhoneNumber(searchQuery.value, validationEntities.NORMAL_CALL)
        .valid
    ) {
      phoneNumberItem.value = {
        id: `phone_${searchQuery.value}`,
        name: searchQuery.value,
        type: "External Number",
        isPhoneNumber: true,
      };
      handleItemSelect(phoneNumberItem.value);
    } else {
      phoneNumberItem.value = null;
    }
  } catch (error) {
    console.error("An error occurred while handling input search: ", error);
  }
};

const fetchData = async () => {
  try {
    isLoading.value = true;

    const [users, teams, { phoneNumbers, currentUserId }] = await Promise.all([
      toFetchUsers(),
      toFetchTeams(),
      toGetPhoneNumbers({}, true),
    ]);

    const usersWithPhoneNumbers = new Set(
      phoneNumbers
        .filter((phone) => phone.user && phone.phone_number)
        .map((phone) => phone.user.id),
    );

    const teamsWithPhoneNumbers = new Set(
      phoneNumbers
        .filter((phone) => phone.team && phone.phone_number)
        .map((phone) => phone.team.id),
    );

    usersList.value = users.filter(
      (user) => usersWithPhoneNumbers.has(user.id) && user.id !== currentUserId,
    );

    teamsList.value = teams.filter((team) =>
      teamsWithPhoneNumbers.has(team.id),
    );
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    isLoading.value = false;
  }
};

const handleToTransferCall = async () => {
  try {
    isTransferringCall.value = true;
    if (formState.transferNumber) {
      const validationResponse = validatePhoneNumber(
        formState.transferNumber,
        validationEntities.TRANSFER_CALL,
      );

      if (!validationResponse.valid) {
        message.error(validationResponse.error);
        return;
      }
    }
    const params = {
      type: formState?.type,
      typeId: formState?.typeId,
      transferNumber: formState?.transferNumber,
      transferName: selectedItem.value?.name,
    };

    await toTransferCall(params);
  } catch (error) {
    console.error("An error occurred while handling call transfer: ", error);
  } finally {
    isTransferringCall.value = false;
  }
};

const handleWarmTransfer = async () => {
  try {
    isWarmTransferring.value = true;
    if (formState.transferNumber) {
      const validationResponse = validatePhoneNumber(
        formState.transferNumber,
        validationEntities.TRANSFER_CALL,
      );

      if (!validationResponse.valid) {
        message.error(validationResponse.error);
        return;
      }
    }
    const params = {
      type: formState?.type,
      typeId: formState?.typeId,
      transferNumber: formState?.transferNumber,
      transferName: selectedItem.value?.name,
    };

    const response = await toExecuteWarmTransfer(params);
    if (response?.success) {
      console.log("Warm transferred successfully!!");
    }
  } catch (error) {
    console.error("An error occurred while handling warm transfer: ", error);
  } finally {
    isWarmTransferring.value = false;
  }
};

watch(searchQuery, (newValue) => {
  if (!newValue) {
    phoneNumberItem.value = null;
  }
});

watch(filteredItems, (newItems) => {
  if (
    selectedItem.value &&
    !newItems.some((item) => item.id === selectedItem.value.id)
  ) {
    selectedItem.value = null;
    formState.type = assignCallTypes.USER;
    formState.typeId = null;
    formState.transferNumber = "";
  }
});

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.selected {
  border-radius: 8px;
  background: var(--white-opacity-6);
  transition: all 0.3s ease-in-out;
}

.hover_effect:hover {
  background: var(--white-opacity-8);
  border-radius: 8px;
}
</style>
